import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Banknote, Handshake, Landmark, LineChart, Radar, RefreshCw, Users } from 'lucide-react';
import { AppShell } from '@/components/shell/AppShell';
import type { RailItem } from '@/components/shell/IconRail';
import { CommandPalette } from '@/components/search/CommandPalette';
import type { SearchHit } from '@/lib/search';
import { apiBaseUrl } from '@/lib/apiBase';
import { CashFlowView, type CashFlowFilterRequest } from '@/components/cash-flow/CashFlowView';
import type { ActiveMatch } from '@/components/cash-flow/CashFlowWorkspace';
import type { LoansFocus } from '@/components/loans/LoansView';
import type { PlansFocus } from '@/components/payment-plans/PaymentPlansView';
import type { ProjectsFocus } from '@/components/projects/ProjectsView';
import type { PayrollFocus } from '@/components/loans/PayrollPanel';
import { ViewErrorBoundary } from '@/components/shell/ViewErrorBoundary';
import { LoginView } from '@/components/auth/LoginView';
import { ResetPasswordView } from '@/components/auth/ResetPasswordView';
import { useAuth } from '@/auth/useAuth';
import { getSupabase } from '@/auth/supabaseClient';

// The primary view (cash flow) loads eagerly — it is the hub and the default
// landing view. Every secondary view is code-split behind React.lazy so the
// first paint never pays for modules the user has not opened (dev serves each
// module individually, so this directly cuts the initial request count; in
// the production build it becomes separate chunks).
const SettingsView = lazy(() =>
  import('@/components/settings/SettingsView').then((m) => ({ default: m.SettingsView })),
);
const SyncManagementView = lazy(() =>
  import('@/components/sync/SyncManagementView').then((m) => ({ default: m.SyncManagementView })),
);
const LoansView = lazy(() =>
  import('@/components/loans/LoansView').then((m) => ({ default: m.LoansView })),
);
const PaymentPlansView = lazy(() =>
  import('@/components/payment-plans/PaymentPlansView').then((m) => ({
    default: m.PaymentPlansView,
  })),
);
const PayrollView = lazy(() =>
  import('@/components/loans/PayrollView').then((m) => ({ default: m.PayrollView })),
);
const ForecastView = lazy(() =>
  import('@/components/forecast/ForecastView').then((m) => ({ default: m.ForecastView })),
);
const ProjectsView = lazy(() =>
  import('@/components/projects/ProjectsView').then((m) => ({ default: m.ProjectsView })),
);
const BankView = lazy(() =>
  import('@/components/bank/BankView').then((m) => ({ default: m.BankView })),
);
const SupplierRadarView = lazy(() =>
  import('@/components/supplier-radar/SupplierRadarView').then((m) => ({
    default: m.SupplierRadarView,
  })),
);

/** A central-search hit / deep-link href into the primary view, e.g. `/rahavoog?entry=PR-466`. */
const ENTRY_HREF = /^\/rahavoog\?entry=(.+)$/;
/** A central-search hit / deep-link href into Laenud, e.g. `/laenud?loan=<id>`. */
const LOAN_HREF = /^\/laenud\?loan=(.+)$/;
/** A central-search hit / deep-link href into Maksekokkulepped, e.g. `/maksekokkulepped?plan=<id>`. */
const PLAN_HREF = /^\/maksekokkulepped\?plan=(.+)$/;
/** A central-search hit / deep-link href into Palgafond, e.g. `/palgafond?kuu=2026-03`. */
const PAYROLL_HREF = /^\/palgafond\?kuu=(.+)$/;
/** A central-search hit / deep-link href into Prognoosid (`/prognoosid`). */
const FORECAST_HREF = /^\/prognoosid(?:\?.*)?$/;
/** A central-search hit / deep-link href into Projektid, e.g. `/projektid/PR-466`. */
const PROJECT_HREF = /^\/projektid\/(.+)$/;

// Estonian nav labels (UI labels stay Estonian). Icons are short glyph
// stand-ins until the icon set lands; real routes arrive with their modules.
const NAV: Omit<RailItem, 'active'>[] = [
  { id: 'calendar', label: 'Kalender', icon: '▦' },
  { id: 'loans', label: 'Laenud', icon: <Landmark className="h-4 w-4" /> },
  { id: 'plans', label: 'Maksekokkulepped', icon: <Handshake className="h-4 w-4" /> },
  { id: 'bank', label: 'Pank', icon: <Banknote className="h-4 w-4" /> },
  { id: 'payroll', label: 'Palgafond', icon: <Users className="h-4 w-4" /> },
  { id: 'forecast', label: 'Prognoosid', icon: <LineChart className="h-4 w-4" /> },
  { id: 'projects', label: 'Projektid', icon: '◳' },
  { id: 'supplier-radar', label: 'Tarnijate radar', icon: <Radar className="h-4 w-4" /> },
  { id: 'kpi', label: 'Ettevõtte KPI', icon: '◷' },
  { id: 'sync', label: 'Sünkroon', icon: <RefreshCw className="h-4 w-4" /> },
  { id: 'settings', label: 'Seaded', icon: '⚙' },
];

/** Seed the loans deep-link focus from the URL on first load (`?loan=`). */
function initialLoanFocus(): LoansFocus | null {
  const loanId = new URLSearchParams(window.location.search).get('loan');
  return loanId ? { loanId, nonce: 0 } : null;
}

/** Seed the payroll deep-link focus from the URL on first load (`?kuu=`). */
function initialPayrollFocus(): PayrollFocus | null {
  const month = new URLSearchParams(window.location.search).get('kuu');
  return month ? { month, nonce: 0 } : null;
}

/** Seed the payment-plans deep-link focus from the URL on first load (`?plan=`). */
function initialPlanFocus(): PlansFocus | null {
  const planId = new URLSearchParams(window.location.search).get('plan');
  return planId ? { planId, nonce: 0 } : null;
}

/** Seed the projects deep-link focus from the URL on first load (`/projektid/<code>`). */
function initialProjectFocus(): ProjectsFocus | null {
  const match = PROJECT_HREF.exec(window.location.pathname);
  return match ? { code: decodeURIComponent(match[1]), nonce: 0 } : null;
}

export default function App() {
  const [searchOpen, setSearchOpen] = useState(false);
  // A loans / payroll / forecast deep link in the URL lands the user straight in that view.
  const [active, setActive] = useState(() =>
    initialLoanFocus()
      ? 'loans'
      : initialPlanFocus()
        ? 'plans'
        : initialPayrollFocus()
          ? 'payroll'
          : /^\/prognoosid$/.test(window.location.pathname)
            ? 'forecast'
            : /^\/projektid(\/.*)?$/.test(window.location.pathname)
              ? 'projects'
              : 'calendar',
  );
  // A "locate" request bridged into the primary view: the central-search (Cmd+K) result
  // or a `?entry=` deep-link asks the CURRENT lens to reveal the document (drop the old
  // "Ava kalendris" behaviour — locate works in whichever lens is open, L14). Seeded from
  // the URL on first load; a fresh nonce on every selection re-fires the reveal.
  const [locateRequest, setLocateRequest] = useState<ActiveMatch | null>(() => {
    const entry = new URLSearchParams(window.location.search).get('entry');
    return entry ? { entryId: entry, nonce: 0 } : null;
  });
  // Tarnijate radar "Ava rahavoos" → the supplier's name lands in the shared
  // cash-flow free-text search, so the calendar/table open already narrowed.
  const [cashFilterRequest, setCashFilterRequest] = useState<CashFlowFilterRequest | null>(null);
  // Leaving the cash-flow view consumes the pending filter request — CashFlowView
  // unmounts (its once-per-nonce ref resets), so without this a cleared supplier
  // search would silently re-apply on the next visit. locateRequest is NOT cleared
  // here on purpose: a locate fired from another view intentionally defers its
  // reveal until the cash-flow view is next opened.
  useEffect(() => {
    if (active !== 'calendar') setCashFilterRequest(null);
  }, [active]);
  // Laenud (`/laenud?loan=`) and Palgafond (`/palgafond?kuu=`) deep-link focus — each seeded
  // from the URL, re-fired with a fresh nonce on every central-search selection.
  const [loanFocus, setLoanFocus] = useState<LoansFocus | null>(initialLoanFocus);
  const [planFocus, setPlanFocus] = useState<PlansFocus | null>(initialPlanFocus);
  const [payrollFocus, setPayrollFocus] = useState<PayrollFocus | null>(initialPayrollFocus);
  const [projectFocus, setProjectFocus] = useState<ProjectsFocus | null>(initialProjectFocus);
  // The real Auth module: production shows the Estonian login form, development
  // keeps the auto-login convenience (src/auth/useAuth). Every /api/* request
  // carries a genuine Supabase token (RLS applies, L7).
  const { status, token, error, passwordRecovery, signIn, requestPasswordReset, updatePassword, signOut } =
    useAuth();
  // In production the API lives on a separate Workers origin (VITE_API_URL,
  // injected at build time — see src/lib/apiBase.ts); in dev the base is ''
  // and every /api/* call stays same-origin through the Vite proxy.
  // `accessToken` freezes the render-time token into every request; long batch
  // runs (the bank AI pass takes minutes) outlive it, so `getAccessToken` lets
  // them re-read the LIVE session token per batch (mid-run 401s seen in prod).
  const options = useMemo(
    () =>
      token
        ? {
            accessToken: token,
            getAccessToken: async () => (await getSupabase().auth.getSession()).data.session?.access_token ?? null,
            baseUrl: apiBaseUrl() || undefined,
          }
        : undefined,
    [token],
  );

  // A central-search selection: if it targets the primary view (`/rahavoog?entry=`), keep
  // the user in their current lens and locate the document there; a loans / payroll href
  // switches to that rail item and focuses the target; any other href navigates.
  const handleSearchSelect = (hit: SearchHit) => {
    const href = hit.href;
    const match = href ? ENTRY_HREF.exec(href) : null;
    const loanMatch = href ? LOAN_HREF.exec(href) : null;
    const planMatch = href ? PLAN_HREF.exec(href) : null;
    const payrollMatch = href ? PAYROLL_HREF.exec(href) : null;
    const projectMatch = href ? PROJECT_HREF.exec(href) : null;
    if (match) {
      const entryId = decodeURIComponent(match[1]);
      setActive((a) => (a === 'settings' || a === 'sync' ? 'calendar' : a));
      setSearchOpen(false);
      window.history.replaceState(null, '', href);
      setLocateRequest((r) => ({ entryId, nonce: (r?.nonce ?? 0) + 1 }));
    } else if (loanMatch) {
      const loanId = decodeURIComponent(loanMatch[1]);
      setActive('loans');
      setSearchOpen(false);
      window.history.replaceState(null, '', href);
      setLoanFocus((f) => ({ loanId, nonce: (f?.nonce ?? 0) + 1 }));
    } else if (planMatch) {
      const planId = decodeURIComponent(planMatch[1]);
      setActive('plans');
      setSearchOpen(false);
      window.history.replaceState(null, '', href);
      setPlanFocus((f) => ({ planId, nonce: (f?.nonce ?? 0) + 1 }));
    } else if (payrollMatch) {
      const month = decodeURIComponent(payrollMatch[1]);
      setActive('payroll');
      setSearchOpen(false);
      window.history.replaceState(null, '', href);
      setPayrollFocus((f) => ({ month, nonce: (f?.nonce ?? 0) + 1 }));
    } else if (projectMatch) {
      const code = decodeURIComponent(projectMatch[1]);
      setActive('projects');
      setSearchOpen(false);
      window.history.replaceState(null, '', href);
      setProjectFocus((f) => ({ code, nonce: (f?.nonce ?? 0) + 1 }));
    } else if (href && FORECAST_HREF.test(href)) {
      setActive('forecast');
      setSearchOpen(false);
      window.history.replaceState(null, '', href);
    } else if (href) {
      window.location.assign(href);
    }
  };

  // A Supabase recovery-link session: show the new-password screen instead of
  // the shell (or the login form) until the password is saved.
  if (passwordRecovery) {
    return <ResetPasswordView onSubmit={updatePassword} />;
  }

  if (status === 'loading' || !token) {
    if (status === 'signedOut') {
      return <LoginView onSubmit={signIn} onRequestReset={requestPasswordReset} initialError={error} />;
    }
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Autentimine…
      </div>
    );
  }

  const navItems: RailItem[] = NAV.map((item) => ({ ...item, active: item.id === active }));

  return (
    <>
      <AppShell
        navItems={navItems}
        onOpenSearch={() => setSearchOpen(true)}
        onSelectNav={setActive}
        actions={
          <button
            type="button"
            onClick={() => void signOut()}
            className="h-8 rounded-md border border-border bg-background px-3 text-xs text-muted-foreground transition-colors hover:bg-accent"
          >
            Logi välja
          </button>
        }
      >
        <ViewErrorBoundary>
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Laadin vaadet…
              </div>
            }
          >
            {active === 'settings' ? (
              <SettingsView options={options} />
            ) : active === 'sync' ? (
              <SyncManagementView options={options} />
            ) : active === 'loans' ? (
              <LoansView options={options} focus={loanFocus} />
            ) : active === 'plans' ? (
              <PaymentPlansView options={options} focus={planFocus} />
            ) : active === 'payroll' ? (
              <PayrollView options={options} focus={payrollFocus} />
            ) : active === 'forecast' ? (
              <ForecastView options={options} />
            ) : active === 'bank' ? (
              <BankView options={options} />
            ) : active === 'supplier-radar' ? (
              <SupplierRadarView
                options={options}
                onLocateEntry={(entryId) => {
                  setActive('calendar');
                  setLocateRequest((r) => ({ entryId, nonce: (r?.nonce ?? 0) + 1 }));
                }}
                onOpenInCashflow={(party) => {
                  // Narrow every cash-flow lens to the supplier via the SHARED
                  // free-text search (L14) — not just switch views unfiltered.
                  setCashFilterRequest((r) => ({ search: party, nonce: (r?.nonce ?? 0) + 1 }));
                  setActive('calendar');
                }}
              />
            ) : active === 'projects' ? (
              <ProjectsView options={options} focus={projectFocus} />
            ) : (
              <CashFlowView options={options} locateRequest={locateRequest} filterRequest={cashFilterRequest} />
            )}
          </Suspense>
        </ViewErrorBoundary>
      </AppShell>
      <CommandPalette
        open={searchOpen}
        onOpenChange={setSearchOpen}
        searchOptions={options}
        onSelect={handleSearchSelect}
      />
    </>
  );
}
